import React from 'react';
import { LinkWithTooltip } from '@/components/ui/LinkWithTooltip';
import { OfferCounterInfo } from '@/types/offer';
import { PoolDetail, PoolStatus } from '@/types/pool';
import { formatCurrency, formatDateToShortString, formatPercentage } from '@/utils/misc';
import { computeLoanDetails, getPoolTermsData, calculateLTV } from '@/utils/poolCalculations';
import { formatTermDisplay, getLoanTypeLabel, getPropertyOccupancyLabel } from '@/utils/poolCreateUtils';
import { POOL_TYPE_DISPLAY, isRefinancing } from '@/utils/poolDisplay';
import { getAcceptedCounterOfferInfo } from '@/utils/poolStatus';
import { CounterInfoField } from './CounterInfoField';
import { InfoField } from './InfoField';
import { InfoFieldWithCounter } from './InfoFieldWithCounter';

type PoolOverviewContentProps = {
  pool: PoolDetail;
  isInvestor: boolean;
  counterOfferInfo: OfferCounterInfo | null;
};

export const PoolOverviewContent: React.FC<PoolOverviewContentProps> = ({ pool, isInvestor, counterOfferInfo }) => {
  const property = pool.property;
  const fullAddress = property.address
    ? `${property.address.address_line1 || ''}, ${property.address.city || ''}, ${property.address.state || ''}, ${
        property.address.zip_code || ''
      }`
    : '--';

  const noteLabel = isInvestor ? 'Counter Offer note sent to the Borrower' : 'Counter Offer note from the Investor';
  // Find counter offer
  const counterInfo = counterOfferInfo;
  const acceptedCounterInfo = getAcceptedCounterOfferInfo(pool.offers);
  const showCounterOfferFields = pool.status === PoolStatus.AWAITING_INVESTORS && !!counterInfo && !acceptedCounterInfo;
  const poolOverviewData = getPoolTermsData(pool);
  const loansRemainingBalances = pool.property.existing_loans?.map((loan) => loan.remaining_balance) ?? [];
  const poolIsRefinancing = isRefinancing(pool.pool_type);

  const poolLoanDetails = computeLoanDetails(
    parseFloat(poolOverviewData.requestedAmount),
    parseFloat(poolOverviewData.roiRate),
    poolOverviewData.loanType,
    parseInt(poolOverviewData.term.toString())
  );

  const ltv = calculateLTV(
    parseFloat(poolOverviewData.requestedAmount),
    parseFloat(property.details.property_value),
    loansRemainingBalances,
    poolIsRefinancing
  );

  const finalRepayment = poolLoanDetails.overallRepay;
  const roi_amount = formatCurrency(poolLoanDetails.roiAmount);
  const monthlyPayment = poolLoanDetails.monthlyPayment;

  const counterLoanDetails =
    counterInfo?.amount && counterInfo?.roi_rate && counterInfo?.term_months
      ? computeLoanDetails(
          parseFloat(counterInfo?.amount),
          parseFloat(counterInfo?.roi_rate),
          poolOverviewData.loanType,
          counterInfo?.term_months
        )
      : null;

  const counterLTV = counterInfo?.amount
    ? calculateLTV(
        parseFloat(counterInfo.amount),
        parseFloat(property.details.property_value),
        loansRemainingBalances,
        poolIsRefinancing
      )
    : null;

  const counterMonthlyPayment = counterLoanDetails?.monthlyPayment;
  const counterFinalRepayment = counterLoanDetails?.overallRepay;
  const counterROI = counterLoanDetails?.roiAmount;

  const counterOfferFields = showCounterOfferFields
    ? {
        requestedAmount: counterInfo?.amount ? formatCurrency(counterInfo.amount) : undefined,
        repaymentAmount: counterFinalRepayment ? formatCurrency(counterFinalRepayment) : undefined,
        roi: counterROI ? formatCurrency(counterROI) : undefined,
        monthlyPayment: counterMonthlyPayment ? formatCurrency(counterMonthlyPayment.toFixed(2)) : undefined,
        interestRate: counterInfo?.roi_rate ? formatPercentage(counterInfo.roi_rate) : undefined,
        term: counterInfo?.term_months ? formatTermDisplay(counterInfo.term_months) : undefined,
        closingCost: counterLoanDetails ? formatCurrency(counterLoanDetails.closingCost) : undefined,
        note: counterInfo?.note,
        ltv: counterLTV ? formatPercentage(counterLTV) : undefined,
      }
    : null;

  return (
    <div className="w-full flex flex-col justify-start items-start gap-5">
      {/* Pool Repayment Section */}
      <div className="w-full flex flex-col sm:flex-row justify-start items-start sm:items-end gap-4 sm:gap-10">
        <div className="flex flex-row justify-start items-center gap-2">
          <div className="flex flex-col justify-start items-start gap-2">
            <SectionHeader title="Requested Amount" />
            <div className="flex items-center gap-2">
              <div
                className="text-black text-3xl font-medium break-words"
                style={{
                  fontFamily: 'var(--ep-font-avenir)',
                }}
              >
                {formatCurrency(poolOverviewData.requestedAmount)}
              </div>
            </div>
          </div>
          {counterOfferFields?.requestedAmount && (
            <div className="self-end">
              <CounterInfoField value={counterOfferFields.requestedAmount} size="large" />
            </div>
          )}
        </div>
        <InfoField className="mb-1" label="Date Created" value={formatDateToShortString(pool.created_at)} />
      </div>
      {/* Pool Information Section */}
      <div className="w-full flex flex-col justify-start items-start gap-5">
        <SectionHeader title="Pool Information" />
        <div className="w-full grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
          <InfoField label="Pool Type" value={POOL_TYPE_DISPLAY[pool.pool_type]} />
          {!isInvestor && (
            <InfoFieldWithCounter
              label="Estimated Closing Cost"
              value={formatCurrency(poolLoanDetails.closingCost)}
              counterValue={counterOfferFields?.closingCost}
            />
          )}
          <InfoFieldWithCounter
            label="Estimated Final Repayment"
            value={formatCurrency(finalRepayment)}
            counterValue={counterOfferFields?.repaymentAmount}
          />
          {isInvestor && <InfoFieldWithCounter label="ROI" value={roi_amount} counterValue={counterOfferFields?.roi} />}
          <InfoFieldWithCounter
            label="Estimated Monthly Payment"
            value={formatCurrency(monthlyPayment.toFixed(2))}
            counterValue={counterOfferFields?.monthlyPayment}
          />
          <InfoFieldWithCounter
            label="Interest rate"
            value={formatPercentage(poolOverviewData.roiRate)}
            counterValue={counterOfferFields?.interestRate}
          />
          <InfoField label="Pool Program" value={getLoanTypeLabel(pool.loan_terms.loan_type)} />
          <InfoFieldWithCounter
            label="Terms"
            value={formatTermDisplay(poolOverviewData.term)}
            counterValue={counterOfferFields?.term}
          />
          <InfoFieldWithCounter
            label="Estimated Loan To Value (LTV)"
            value={formatPercentage(ltv)}
            counterValue={counterOfferFields?.ltv}
          />
        </div>
      </div>

      <div className="w-full mt-2">
        <div className="bg-gray-50 rounded-lg p-4" style={{ fontFamily: 'var(--ep-font-avenir)' }}>
          <InfoField label="Note From Borrower" value={pool.note || '--'} />
        </div>
      </div>

      <div className="w-full flex flex-col justify-start items-start gap-5">
        <SectionHeader title="Borrower Info" />
        <div className="w-full grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
          <InfoField
            label="Name"
            value={pool.applicant ? `${pool.applicant.first_name} ${pool.applicant.last_name}` : '--'}
          />
        </div>
      </div>

      {/* Property Info Section */}
      <div className="w-full flex flex-col justify-start items-start gap-5">
        <SectionHeader title="Property Info" />
        <div className="w-full grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
          <InfoField label="Address" value={fullAddress} />
          <InfoField label="Residence Type" value={getPropertyOccupancyLabel(property.details.occupancy_status)} />
          <InfoField label="Property value" value={formatCurrency(property.details.property_value)} />
          <InfoField
            label="Property Links"
            value={
              property.links && property.links.length > 0 ? (
                <div className="flex flex-col justify-start items-start gap-2 w-full min-w-0">
                  {property.links?.map((link, index) => (
                    <LinkWithTooltip key={index} url={link.url} />
                  ))}
                </div>
              ) : (
                '--'
              )
            }
          />
          <InfoField
            label="Existing loans' Remaining Balances"
            value={
              property.existing_loans && property.existing_loans.length > 0 ? (
                <div className="flex flex-col">
                  {property.existing_loans.map((loan, index) => (
                    <div key={index}>
                      Loan {index + 1}: {formatCurrency(loan.remaining_balance)}
                    </div>
                  ))}
                </div>
              ) : (
                '--'
              )
            }
          />
        </div>
      </div>

      {/* Counter Offer Note */}
      {showCounterOfferFields && counterOfferFields?.note && (
        <CounterInfoField
          value={counterOfferFields.note}
          label={noteLabel}
          size="large"
          color="text-[#D1863B]"
          showArrow={false}
        />
      )}

      {showCounterOfferFields && (
        <div
          className="w-full flex flex-col justify-start items-start gap-2 border-1 border-solid border-[#E5E7EB] rounded-lg p-4 bg-[#F4F4F4] mt-5"
          style={{ fontFamily: 'var(--ep-font-avenir)' }}
        >
          <div className="text-[#D1863B] text-sm font-medium break-words">Important:</div>
          {isInvestor ? (
            <div className="text-black text-sm font-medium break-words">
              You have submitted a counteroffer and requested additional documents.
              <span className="text-[#D1863B]">
                {' '}
                The borrower has 72 hours to respond. The requested documents must be provided to escrow upon
                acceptance.
              </span>
            </div>
          ) : (
            <div className="text-black text-sm font-medium break-words">
              The investor has submitted a counteroffer and requested additional documents.
              <span className="text-[#D1863B]">
                {' '}
                You 72 hours to respond. The requested documents must be provided to escrow upon acceptance.
              </span>
            </div>
          )}
          <div className="text-black text-sm font-medium break-words mt-2">
            (i)Counter Offer details appear in <span className="text-[#BB8D03]">Yellow color</span>
          </div>
        </div>
      )}
    </div>
  );
};

const SectionHeader: React.FC<{ title: string }> = ({ title }) => {
  return (
    <div className="w-full flex flex-col justify-start items-start gap-2">
      <div className="text-[#113D7B] text-base font-medium break-words" style={{ fontFamily: 'var(--ep-font-avenir)' }}>
        {title}
      </div>
    </div>
  );
};
